// ==================== TELEGRAM ACCOUNT LINKING ====================
// Shared between equipment.html (admin) and mechanic.html (specialist).
// Requires the page's supabase client to already inject x-session-token
// on every request (see the `global.fetch` wrapper in each main.js).

import QRCode from 'qrcode'
import { t } from './i18n.js'

const POLL_INTERVAL_MS = 3000
const POLL_TIMEOUT_MS = 5 * 60 * 1000 // matches the backend code TTL

export function initTelegramLink(supabase) {
  const btn = document.getElementById('telegramBtn')
  const btnText = document.getElementById('telegramBtnText')
  const modal = document.getElementById('telegramModal')
  const closeBtn = document.getElementById('closeTelegramModalBtn')
  const notLinkedView = document.getElementById('telegramNotLinked')
  const linkedView = document.getElementById('telegramLinked')
  const qrCanvas = document.getElementById('telegramQrCanvas')
  const deepLinkEl = document.getElementById('telegramDeepLink')
  const waitingEl = document.getElementById('telegramWaiting')
  const disconnectBtn = document.getElementById('telegramDisconnectBtn')

  if (!btn || !modal) return { refreshStatus: async () => {} }

  // showDashboard()/session-restore flows can call this more than once
  // per page load (logout → login again) — guard against double-binding.
  if (btn.dataset.telegramInit === '1') return { refreshStatus: checkStatus }
  btn.dataset.telegramInit = '1'

  let pollTimer = null
  let pollDeadline = 0

  function setButtonState(linked) {
    const label = linked ? t('telegram.connected_short') : t('telegram.connect')
    if (btnText) btnText.textContent = label
    // The label is hidden when the button collapses to an icon on narrow
    // screens, so the linked state also has to read from the icon itself.
    btn.classList.toggle('is-linked', linked)
    btn.title = label
  }

  function stopPolling() {
    if (pollTimer) clearInterval(pollTimer)
    pollTimer = null
  }

  async function checkStatus() {
    try {
      const { data } = await supabase.functions.invoke('telegram-link', { body: { action: 'status' } })
      setButtonState(!!data?.linked)
      return !!data?.linked
    } catch (err) {
      console.warn('Telegram status check failed:', err)
      return false
    }
  }

  function showLinkedView() {
    notLinkedView.style.display = 'none'
    linkedView.style.display = 'block'
  }

  function showNotLinkedView() {
    notLinkedView.style.display = 'block'
    linkedView.style.display = 'none'
  }

  async function openModal() {
    modal.style.display = 'flex'

    const linked = await checkStatus()
    if (linked) {
      showLinkedView()
      return
    }

    showNotLinkedView()
    waitingEl.textContent = t('telegram.waiting')

    try {
      const { data, error } = await supabase.functions.invoke('telegram-link', { body: { action: 'generate' } })
      if (error || !data?.deep_link) throw error || new Error('No deep link returned')

      deepLinkEl.href = data.deep_link
      await QRCode.toCanvas(qrCanvas, data.deep_link, { width: 220, margin: 1 })

      pollDeadline = Date.now() + POLL_TIMEOUT_MS
      stopPolling()
      pollTimer = setInterval(async () => {
        if (Date.now() > pollDeadline) {
          stopPolling()
          waitingEl.textContent = t('telegram.expired')
          return
        }
        const nowLinked = await checkStatus()
        if (nowLinked) {
          stopPolling()
          showLinkedView()
        }
      }, POLL_INTERVAL_MS)
    } catch (err) {
      console.error('Telegram code generation failed:', err)
      waitingEl.textContent = t('telegram.error')
    }
  }

  function closeModal() {
    modal.style.display = 'none'
    stopPolling()
  }

  async function disconnect() {
    try {
      await supabase.functions.invoke('telegram-link', { body: { action: 'unlink' } })
    } catch (err) {
      console.warn('Telegram disconnect failed:', err)
    }
    setButtonState(false)
    showNotLinkedView()
    closeModal()
  }

  btn.addEventListener('click', openModal)
  closeBtn?.addEventListener('click', closeModal)
  modal.querySelector('.modal-overlay')?.addEventListener('click', closeModal)
  disconnectBtn?.addEventListener('click', disconnect)

  checkStatus()

  return { refreshStatus: checkStatus }
}
